import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatSliderModule } from '@angular/material/slider';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HandlerMessageService } from 'app/common/services/handler-error/handler-message.service';

interface DemoCase {
  title: string;
  description: string;
  icon: string;
  code: string;
  action: () => void;
}

@Component({
  selector: 'app-handler-message-demo',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    MatSliderModule,
    MatTabsModule,
    MatTooltipModule
  ],
  templateUrl: './handler-message-demo.component.html',
  styleUrls: ['./handler-message-demo.component.scss']
})
export class HandlerMessageDemoComponent {
  
  // Para el generador personalizado
  customMessage = 'Este es un mensaje personalizado';
  customType: 'error' | 'warning' | 'success' | 'info' = 'info';
  customDuration = 5000;
  customStatusCode?: number;
  customPosition: 'start' | 'center' | 'end' = 'center';
  customVerticalPosition: 'top' | 'bottom' = 'bottom';

  // Casos de uso predefinidos
  useCases: DemoCase[] = [
    {
      title: 'Error Simple',
      description: 'Error básico sin código de estado',
      icon: 'error',
      code: `this.handlerMessage.error({
  message: 'Error al procesar la solicitud'
});`,
      action: () => this.showSimpleError()
    },
    {
      title: 'Error HTTP 404',
      description: 'Error con código de estado HTTP',
      icon: 'search_off',
      code: `this.handlerMessage.error({
  message: 'Recurso no encontrado',
  statusCode: 404
});`,
      action: () => this.showError404()
    },
    {
      title: 'Error con Fallback',
      description: 'Error con mensaje alternativo',
      icon: 'warning_amber',
      code: `this.handlerMessage.error({
  message: err?.error?.message,
  alternativeMessage: 'Error al cargar datos',
  statusCode: err?.status
});`,
      action: () => this.showErrorWithFallback()
    },
    {
      title: 'Éxito Simple',
      description: 'Mensaje de operación exitosa',
      icon: 'check_circle',
      code: `this.handlerMessage.success({
  message: 'Datos guardados exitosamente',
  statusCode: 200
});`,
      action: () => this.showSuccess()
    },
    {
      title: 'Advertencia',
      description: 'Mensaje de advertencia (duration default)',
      icon: 'warning',
      code: `this.handlerMessage.warning({
  message: 'Los cambios no se han guardado'
});`,
      action: () => this.showWarning()
    },
    {
      title: 'Información',
      description: 'Mensaje informativo (duration default)',
      icon: 'info',
      code: `this.handlerMessage.info({
  message: 'Procesando solicitud...'
});`,
      action: () => this.showInfo()
    }
  ];

  // Casos HTTP específicos
  httpCases: DemoCase[] = [
    {
      title: 'Error 400 - Bad Request',
      description: 'Datos inválidos o mal formados',
      icon: 'cancel',
      code: `statusCode: 400`,
      action: () => this.showHttpError(400, 'Datos inválidos')
    },
    {
      title: 'Error 401 - Unauthorized',
      description: 'Sesión expirada o no autorizado',
      icon: 'lock',
      code: `statusCode: 401`,
      action: () => this.showHttpError(401, 'Tu sesión ha expirado')
    },
    {
      title: 'Error 403 - Forbidden',
      description: 'Sin permisos para la acción',
      icon: 'block',
      code: `statusCode: 403`,
      action: () => this.showHttpError(403, 'No tienes permisos para esta acción')
    },
    {
      title: 'Error 404 - Not Found',
      description: 'Recurso no encontrado',
      icon: 'search_off',
      code: `statusCode: 404`,
      action: () => this.showHttpError(404, 'Recurso no encontrado')
    },
    {
      title: 'Error 409 - Conflict',
      description: 'Conflicto de recursos',
      icon: 'report_problem',
      code: `statusCode: 409`,
      action: () => this.showHttpError(409, 'El recurso ya existe')
    },
    {
      title: 'Error 500 - Server Error',
      description: 'Error interno del servidor',
      icon: 'dns',
      code: `statusCode: 500`,
      action: () => this.showHttpError(500, 'Error en el servidor')
    }
  ];

  // Casos de operaciones CRUD
  crudCases: DemoCase[] = [
    {
      title: 'Crear Registro',
      description: 'Operación CREATE exitosa',
      icon: 'add_circle',
      code: `this.handlerMessage.success({
  message: 'Usuario creado exitosamente'
});`,
      action: () => this.showCreateSuccess()
    },
    {
      title: 'Actualizar Registro',
      description: 'Operación UPDATE exitosa',
      icon: 'update',
      code: `this.handlerMessage.success({
  message: 'Registro actualizado con éxito'
});`,
      action: () => this.showUpdateSuccess()
    },
    {
      title: 'Eliminar Registro',
      description: 'Operación DELETE exitosa',
      icon: 'delete',
      code: `this.handlerMessage.success({
  message: 'Registro eliminado exitosamente'
});`,
      action: () => this.showDeleteSuccess()
    },
    {
      title: 'Error al Crear',
      description: 'Error en operación CREATE',
      icon: 'error',
      code: `this.handlerMessage.error({
  message: err?.error?.message,
  alternativeMessage: 'Error al crear registro',
  statusCode: err?.status
});`,
      action: () => this.showCreateError()
    }
  ];

  // Casos de posicionamiento
  positioningCases: DemoCase[] = [
    {
      title: 'Top Center',
      description: 'Mensaje arriba centrado',
      icon: 'vertical_align_top',
      code: `this.handlerMessage.success({
  message: 'Mensaje arriba centrado',
  horizontalPosition: 'center',
  verticalPosition: 'top'
});`,
      action: () => this.showTopCenter()
    },
    {
      title: 'Top Start',
      description: 'Mensaje arriba a la izquierda',
      icon: 'north_west',
      code: `this.handlerMessage.info({
  message: 'Mensaje arriba izquierda',
  horizontalPosition: 'start',
  verticalPosition: 'top'
});`,
      action: () => this.showTopStart()
    },
    {
      title: 'Top End',
      description: 'Mensaje arriba a la derecha',
      icon: 'north_east',
      code: `this.handlerMessage.warning({
  message: 'Mensaje arriba derecha',
  horizontalPosition: 'end',
  verticalPosition: 'top'
});`,
      action: () => this.showTopEnd()
    },
    {
      title: 'Bottom Center',
      description: 'Mensaje abajo centrado (default)',
      icon: 'vertical_align_bottom',
      code: `this.handlerMessage.success({
  message: 'Mensaje abajo centrado'
});`,
      action: () => this.showBottomCenter()
    },
    {
      title: 'Bottom Start',
      description: 'Mensaje abajo a la izquierda',
      icon: 'south_west',
      code: `this.handlerMessage.error({
  message: 'Mensaje abajo izquierda',
  horizontalPosition: 'start',
  verticalPosition: 'bottom'
});`,
      action: () => this.showBottomStart()
    },
    {
      title: 'Bottom End',
      description: 'Mensaje abajo a la derecha',
      icon: 'south_east',
      code: `this.handlerMessage.info({
  message: 'Mensaje abajo derecha',
  horizontalPosition: 'end',
  verticalPosition: 'bottom'
});`,
      action: () => this.showBottomEnd()
    }
  ];

  // Casos de mensajes default
  defaultMessageCases: DemoCase[] = [
    {
      title: 'Error sin Mensaje',
      description: 'Usa mensaje default "Ha ocurrido un error"',
      icon: 'error_outline',
      code: `this.handlerMessage.error({
  alternativeMessage: 'Operación fallida'
});`,
      action: () => this.showErrorDefault()
    },
    {
      title: 'Warning sin Mensaje',
      description: 'Usa mensaje default "Advertencia"',
      icon: 'warning_amber',
      code: `this.handlerMessage.warning({
  alternativeMessage: 'Verifica los datos'
});`,
      action: () => this.showWarningDefault()
    },
    {
      title: 'Success sin Mensaje',
      description: 'Usa mensaje default "Operación exitosa"',
      icon: 'check_circle_outline',
      code: `this.handlerMessage.success({
  statusCode: 200
});`,
      action: () => this.showSuccessDefault()
    },
    {
      title: 'Info sin Mensaje',
      description: 'Usa mensaje default "Información"',
      icon: 'info_outline',
      code: `this.handlerMessage.info({});`,
      action: () => this.showInfoDefault()
    },
    {
      title: 'Error con Alternative',
      description: 'Usa alternativeMessage cuando no hay message',
      icon: 'swap_horiz',
      code: `this.handlerMessage.error({
  message: undefined,
  alternativeMessage: 'Error al procesar datos',
  statusCode: 500
});`,
      action: () => this.showAlternativeMessage()
    },
    {
      title: 'Success con StatusCode',
      description: 'Muestra statusCode con mensaje default',
      icon: 'tag',
      code: `this.handlerMessage.success({
  alternativeMessage: 'Operación completada',
  statusCode: 201
});`,
      action: () => this.showStatusCodeDefault()
    }
  ];

  // Casos especiales
  specialCases: DemoCase[] = [
    {
      title: 'Validación de Formulario',
      description: 'Campos requeridos faltantes',
      icon: 'check_box',
      code: `this.handlerMessage.warning({
  message: 'Por favor completa todos los campos requeridos'
});`,
      action: () => this.showValidationError()
    },
    {
      title: 'Copiado al Portapapeles',
      description: 'Texto copiado exitosamente',
      icon: 'content_copy',
      code: `this.handlerMessage.success({
  message: 'Texto copiado al portapapeles'
});`,
      action: () => this.showCopySuccess()
    },
    {
      title: 'Descarga Completada',
      description: 'Archivo descargado',
      icon: 'download',
      code: `this.handlerMessage.success({
  message: 'Archivo descargado exitosamente'
});`,
      action: () => this.showDownloadSuccess()
    },
    {
      title: 'Error de Red',
      description: 'Sin conexión a internet',
      icon: 'wifi_off',
      code: `this.handlerMessage.error({
  message: 'No hay conexión a internet',
  statusCode: 0
});`,
      action: () => this.showNetworkError()
    },
    {
      title: 'Timeout',
      description: 'Operación excedió tiempo límite',
      icon: 'schedule',
      code: `this.handlerMessage.warning({
  message: 'La operación está tomando más tiempo',
  duration: 6000
});`,
      action: () => this.showTimeout()
    },
    {
      title: 'Importación Completada',
      description: 'Datos importados exitosamente',
      icon: 'cloud_upload',
      code: `this.handlerMessage.success({
  message: 'Importación completada: 150 registros'
});`,
      action: () => this.showImportSuccess()
    }
  ];

  constructor(private handlerMessage: HandlerMessageService) {}

  // ========================================================================
  // UTILIDADES
  // ========================================================================

  /**
   * Copia código al portapapeles y muestra notificación
   */
  async copyCode(code: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(code);
      this.handlerMessage.success({
        message: 'Código copiado al portapapeles'
      });
    } catch (error) {
      this.handlerMessage.error({
        message: 'Error al copiar código'
      });
    }
  }

  /**
   * Genera el código del generador personalizado formateado
   */
  getGeneratedCode(): string {
    let code = `this.handlerMessage.${this.customType}({\n`;
    code += `  message: '${this.customMessage}'`;
    
    if (this.customStatusCode) {
      code += `,\n  statusCode: ${this.customStatusCode}`;
    }
    
    // Solo incluir duration si es >= 5000 (el default)
    if (this.customDuration >= 5000) {
      code += `,\n  duration: ${this.customDuration}`;
    }
    
    code += `\n});`;
    
    return code;
  }

  // ========================================================================
  // MÉTODOS DE CASOS DE USO BÁSICOS
  // ========================================================================

  showSimpleError() {
    this.handlerMessage.error({
      message: 'Error al procesar la solicitud'
    });
  }

  showError404() {
    this.handlerMessage.error({
      message: 'Recurso no encontrado',
      statusCode: 404
    });
  }

  showErrorWithFallback() {
    this.handlerMessage.error({
      message: undefined, // Simula que el backend no envió mensaje
      alternativeMessage: 'Error al cargar datos del servidor',
      statusCode: 500
    });
  }

  showSuccess() {
    this.handlerMessage.success({
      message: 'Datos guardados exitosamente',
      statusCode: 200
    });
  }

  showWarning() {
    this.handlerMessage.warning({
      message: 'Los cambios no se han guardado'
    });
  }

  showInfo() {
    this.handlerMessage.info({
      message: 'Procesando solicitud...'
    });
  }

  // ========================================================================
  // MÉTODOS DE CASOS HTTP
  // ========================================================================

  showHttpError(statusCode: number, message: string) {
    this.handlerMessage.error({
      message: message,
      statusCode: statusCode
    });
  }

  // ========================================================================
  // MÉTODOS DE CASOS CRUD
  // ========================================================================

  showCreateSuccess() {
    this.handlerMessage.success({
      message: 'Usuario creado exitosamente'
    });
  }

  showUpdateSuccess() {
    this.handlerMessage.success({
      message: 'Registro actualizado con éxito'
    });
  }

  showDeleteSuccess() {
    this.handlerMessage.success({
      message: 'Registro eliminado exitosamente'
    });
  }

  showCreateError() {
    this.handlerMessage.error({
      message: 'El email ya está en uso',
      alternativeMessage: 'Error al crear el registro',
      statusCode: 409
    });
  }

  // ========================================================================
  // MÉTODOS DE CASOS ESPECIALES
  // ========================================================================

  showValidationError() {
    this.handlerMessage.warning({
      message: 'Por favor completa todos los campos requeridos'
    });
  }

  showCopySuccess() {
    this.handlerMessage.success({
      message: 'Texto copiado al portapapeles'
    });
  }

  showDownloadSuccess() {
    this.handlerMessage.success({
      message: 'Archivo descargado exitosamente'
    });
  }

  showNetworkError() {
    this.handlerMessage.error({
      message: 'No se puede conectar al servidor. Verifica tu conexión a internet.',
      statusCode: 0
    });
  }

  showTimeout() {
    this.handlerMessage.warning({
      message: 'La operación está tomando más tiempo del esperado. Por favor intenta nuevamente.',
      duration: 6000
    });
  }

  showImportSuccess() {
    this.handlerMessage.success({
      message: 'Importación completada: 150 registros procesados exitosamente'
    });
  }

  // ========================================================================
  // GENERADOR PERSONALIZADO
  // ========================================================================

  showCustomMessage() {
    const options: any = {
      message: this.customMessage,
      duration: this.customDuration,
      horizontalPosition: this.customPosition,
      verticalPosition: this.customVerticalPosition
    };

    if (this.customStatusCode) {
      options.statusCode = this.customStatusCode;
    }

    switch (this.customType) {
      case 'error':
        this.handlerMessage.error(options);
        break;
      case 'warning':
        this.handlerMessage.warning(options);
        break;
      case 'success':
        this.handlerMessage.success(options);
        break;
      case 'info':
        this.handlerMessage.info(options);
        break;
    }
  }

  // ========================================================================
  // SIMULACIÓN DE CASOS REALES
  // ========================================================================

  simulateHttpRequest() {
    this.handlerMessage.info({
      message: 'Enviando solicitud al servidor...'
    });

    setTimeout(() => {
      this.handlerMessage.success({
        message: 'Datos cargados exitosamente',
        statusCode: 200
      });
    }, 2000);
  }

  simulateFormSubmit() {
    this.handlerMessage.info({
      message: 'Validando formulario...'
    });

    setTimeout(() => {
      this.handlerMessage.warning({
        message: 'El campo "Email" es requerido'
      });
    }, 1000);
  }

  simulateSaveOperation() {
    this.handlerMessage.info({
      message: 'Guardando cambios...'
    });

    setTimeout(() => {
      this.handlerMessage.success({
        message: 'Cambios guardados exitosamente',
        statusCode: 200
      });
    }, 1500);
  }

  simulateDeleteOperation() {
    this.handlerMessage.warning({
      message: 'Procesando eliminación...'
    });

    setTimeout(() => {
      this.handlerMessage.success({
        message: 'Registro eliminado exitosamente',
        statusCode: 200
      });
    }, 1500);
  }

  // ========================================================================
  // MÉTODOS DE POSICIONAMIENTO
  // ========================================================================

  showTopCenter() {
    this.handlerMessage.success({
      message: 'Mensaje arriba centrado',
      horizontalPosition: 'center',
      verticalPosition: 'top'
    });
  }

  showTopStart() {
    this.handlerMessage.info({
      message: 'Mensaje arriba izquierda',
      horizontalPosition: 'start',
      verticalPosition: 'top'
    });
  }

  showTopEnd() {
    this.handlerMessage.warning({
      message: 'Mensaje arriba derecha',
      horizontalPosition: 'end',
      verticalPosition: 'top'
    });
  }

  showBottomCenter() {
    this.handlerMessage.success({
      message: 'Mensaje abajo centrado (posición default)'
    });
  }

  showBottomStart() {
    this.handlerMessage.error({
      message: 'Mensaje abajo izquierda',
      horizontalPosition: 'start',
      verticalPosition: 'bottom'
    });
  }

  showBottomEnd() {
    this.handlerMessage.info({
      message: 'Mensaje abajo derecha',
      horizontalPosition: 'end',
      verticalPosition: 'bottom'
    });
  }

  // ========================================================================
  // MÉTODOS DE MENSAJES DEFAULT
  // ========================================================================

  showErrorDefault() {
    this.handlerMessage.error({
      alternativeMessage: 'Operación fallida'
    });
  }

  showWarningDefault() {
    this.handlerMessage.warning({
      alternativeMessage: 'Verifica los datos'
    });
  }

  showSuccessDefault() {
    this.handlerMessage.success({
      statusCode: 200
    });
  }

  showInfoDefault() {
    this.handlerMessage.info({});
  }

  showAlternativeMessage() {
    this.handlerMessage.error({
      message: undefined,
      alternativeMessage: 'Error al procesar datos',
      statusCode: 500
    });
  }

  showStatusCodeDefault() {
    this.handlerMessage.success({
      alternativeMessage: 'Operación completada',
      statusCode: 201
    });
  }

  // ========================================================================
  // DEMOSTRACIÓN DE SECUENCIA
  // ========================================================================

  runSequenceDemo() {
    this.handlerMessage.info({
      message: 'Paso 1: Validando datos...'
    });

    setTimeout(() => {
      this.handlerMessage.info({
        message: 'Paso 2: Enviando al servidor...'
      });
    }, 2000);

    setTimeout(() => {
      this.handlerMessage.info({
        message: 'Paso 3: Procesando respuesta...'
      });
    }, 4000);

    setTimeout(() => {
      this.handlerMessage.success({
        message: 'Proceso completado exitosamente',
        statusCode: 200
      });
    }, 6000);
  }
}

